import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const SECTION_ITEMS = [
  { id: "work", label: "Work" },
  { id: "experience", label: "Experience" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToSection = (id) => {
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.nav
      data-testid="main-nav"
      initial={{ opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-4 md:top-6 left-1/2 -translate-x-1/2 z-50"
    >
      <div
        className={`flex items-center gap-1 md:gap-2 px-2 md:px-3 py-2 rounded-full border border-white/10 backdrop-blur-2xl transition-colors duration-300 ${
          scrolled ? "bg-black/70" : "bg-black/40"
        }`}
      >
        <Link
          to="/"
          data-testid="nav-logo"
          onClick={() => location.pathname === "/" && window.scrollTo({ top: 0, behavior: "smooth" })}
          className="font-heading text-sm md:text-[15px] font-medium text-white px-3 md:px-4 py-1.5 rounded-full hover:bg-white/5 transition-colors"
        >
          A.R.U.
        </Link>
        <div className="hidden md:flex items-center">
          {SECTION_ITEMS.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-link-${item.id}`}
              onClick={() => scrollToSection(item.id)}
              className="text-[13px] text-zinc-300 hover:text-white px-4 py-1.5 rounded-full hover:bg-white/5 transition-colors"
            >
              {item.label}
            </button>
          ))}
          <button
            data-testid="nav-link-blog"
            onClick={() => scrollToSection("journal")}
            className="text-[13px] text-zinc-300 hover:text-white px-4 py-1.5 rounded-full hover:bg-white/5 transition-colors"
          >
            Blog
          </button>
          <button
            data-testid="nav-link-contact"
            onClick={() => scrollToSection("contact")}
            className="text-[13px] text-zinc-300 hover:text-white px-4 py-1.5 rounded-full hover:bg-white/5 transition-colors"
          >
            Contact
          </button>
        </div>
        <button
          data-testid="nav-cta-contact"
          onClick={() => scrollToSection("contact")}
          className="ml-1 text-[13px] font-medium text-black bg-white px-3 md:px-4 py-1.5 rounded-full hover:scale-[1.04] transition-transform inline-flex items-center gap-1"
        >
          Get in touch
          <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      </div>
    </motion.nav>
  );
}
